import area from '@turf/area';
import Terraformer from 'terraformer-arcgis-parser';
import MapSettings from 'components/Map/settings';

const SQM_PER_SQKM = 1_000_000;

const AUSTRALIA_BBOX = {
  xmin: 95,
  xmax: 170,
  ymin: -45,
  ymax: -7
};

/**
 * Returns true when the outer ring of a GeoJSON Polygon (passed as `any`
 * to avoid TS-specific 'as' casts) falls outside Australia's bounding box.
 *
 * WHY any: the project linter is a non-TS-aware ESLint version that rejects
 * type assertion ('as') syntax. Accepting 'any' is the only way to access
 * .coordinates without a cast.
 */
function isOutsideAustralia(polygon: any) {
  const coords = polygon.coordinates[0];
  var xmin = 180,
    xmax = -180,
    ymin = 90,
    ymax = -90;
  for (var i = 0; i < coords.length; i++) {
    var lon = coords[i][0],
      lat = coords[i][1];
    if (lon < xmin) xmin = lon;
    if (lon > xmax) xmax = lon;
    if (lat < ymin) ymin = lat;
    if (lat > ymax) ymax = lat;
  }
  return (
    xmin > AUSTRALIA_BBOX.xmax ||
    xmax < AUSTRALIA_BBOX.xmin ||
    ymin > AUSTRALIA_BBOX.ymax ||
    ymax < AUSTRALIA_BBOX.ymin
  );
}

/**
 * ArcGIS modules needed by the geometry helpers.
 * Callers pass these in so the functions stay decoupled from
 * window globals and can be used with any map instance.
 */
export interface ArcGISGeometryModules {
  GeometryEngine: any;
  WebMercatorUtils: any;
  Graphic: any;
}

export interface ProcessedDrawing {
  // parsedGeoJson is verified to be Polygon before use; typed as GeometryObject
  // to avoid TS-specific 'as' casts which the project linter does not support.
  polygonInGeoJson: GeoJSON.GeometryObject;
  centroid: { latitude: number; longitude: number };
  geometryArea: number;
  geometry: any;
  graphic: any;
}

export class DrawingValidationError extends Error {
  constructor(message: string, name?: string) {
    super(message);
    if (name) this.name = name;
  }
}

/**
 * Computes geodesic area (km²) from a WGS84 GeoJSON polygon.
 *
 * WHY Turf: the member-lookup backend validates uploaded/drawn shapes with Turf
 * on WGS84 GeoJSON. Using the same library on the same coordinates keeps the
 * frontend area limits aligned with server-side totals.
 */
export function computeGeoJsonAreaKm2(polygon: any): number {
  return area(polygon) / SQM_PER_SQKM;
}

/**
 * Buffers a polyline geometry into a 10m-wide polygon strip.
 */
export function bufferPolylineToPolygon(
  geometry: any,
  modules: ArcGISGeometryModules,
  baseMapTheme: string
): any {
  const { GeometryEngine, Graphic } = modules;
  const buffer = GeometryEngine.geodesicBuffer(geometry, 10, 'meters');
  return new Graphic({
    geometry: buffer,
    symbol:
      baseMapTheme === 'dark'
        ? MapSettings.polygonSymbolDarkCompleteDraw
        : MapSettings.polygonSymbol
  });
}

/**
 * Buffers an undersized polygon so both extent dimensions reach ~10m.
 * Returns null if the geometry self-intersects after buffering.
 */
export function bufferSmallPolygon(
  geometry: any,
  modules: ArcGISGeometryModules,
  baseMapTheme: string
): any | null {
  const { GeometryEngine, Graphic } = modules;
  const lowestMeasurement = Math.min(
    geometry.extent.width,
    geometry.extent.height
  );
  // Division by 2 keeps the result close to 10m without over-distorting the shape
  const buffer = GeometryEngine.geodesicBuffer(
    geometry,
    (10 - Math.floor(lowestMeasurement)) / 2,
    'meters'
  );
  if (!buffer) return null;

  return new Graphic({
    geometry: buffer,
    symbol:
      baseMapTheme === 'dark'
        ? MapSettings.polygonSymbolDarkCompleteDraw
        : MapSettings.polygonSymbol
  });
}

/**
 * Validates a completed drawing graphic and returns processed GeoJSON + metadata.
 *
 * This contains the same validation rules as the lodge-enquiry drawing flow:
 * - Polylines are buffered into a 10m polygon strip
 * - Circles smaller than 10m diameter are rejected
 * - Small polygons are buffered to a minimum 10m extent
 * - Self-intersecting polygons are rejected
 * - Area must not exceed maxAreaKm2
 * - Shape must fall within Australia's bounding box
 *
 * Unlike drawingFunction.ts, this function is decoupled from window globals
 * so it works with both the global singleton map and the isolated member lookup map.
 */
export function validateAndProcessDrawing(
  graphic: any,
  tool: string,
  baseMapTheme: string,
  modules: ArcGISGeometryModules,
  maxAreaKm2: number = 20
): ProcessedDrawing {
  const { WebMercatorUtils } = modules;

  let processed = graphic;

  if (
    !processed?.geometry ||
    (processed.geometry.type !== 'polygon' &&
      processed.geometry.type !== 'polyline')
  ) {
    throw new DrawingValidationError('Invalid type geometry.');
  }

  if (processed.geometry.type === 'polyline') {
    processed = bufferPolylineToPolygon(
      processed.geometry,
      modules,
      baseMapTheme
    );
  }

  if (tool === 'circle' && processed.geometry.extent.width < 10) {
    throw new DrawingValidationError('Circle diameter must be more than 10m.');
  }

  if (
    tool === 'polygon' &&
    (processed.geometry.extent.width < 10 ||
      processed.geometry.extent.height < 10)
  ) {
    const buffered = bufferSmallPolygon(
      processed.geometry,
      modules,
      baseMapTheme
    );
    if (!buffered) {
      throw new DrawingValidationError(
        'Self-intersecting polygon has been detected and is not supported.'
      );
    }
    processed = buffered;
  }

  const geometry = processed.geometry;
  const geoGraphicGeometry = WebMercatorUtils.webMercatorToGeographic(geometry);
  const parsedGeoJson = Terraformer.parse(geoGraphicGeometry);

  if (!parsedGeoJson || parsedGeoJson.type !== 'Polygon') {
    throw new DrawingValidationError('Invalid type geometry.');
  }

  const geometryArea = computeGeoJsonAreaKm2(parsedGeoJson);

  processed.symbol =
    baseMapTheme === 'dark'
      ? MapSettings.polygonSymbolDarkCompleteDraw
      : MapSettings.polygonSymbol;

  if (geometry.isSelfIntersecting || geometry.rings.length > 1) {
    throw new DrawingValidationError(
      'Self-intersecting polygon has been detected and is not supported.'
    );
  }

  if (geometryArea > maxAreaKm2) {
    const err = new DrawingValidationError(
      `The maximum is ${maxAreaKm2} square kilometres but your area is ${geometryArea.toFixed(
        1
      )} km²`
    );
    err.name = 'The shape is too large';
    throw err;
  }

  if (isOutsideAustralia(parsedGeoJson)) {
    const err = new DrawingValidationError(
      'The member lookup area must be within Australia'
    );
    err.name = 'Illegal job site';
    throw err;
  }

  return {
    polygonInGeoJson: parsedGeoJson,
    centroid: {
      latitude: geometry.centroid.latitude,
      longitude: geometry.centroid.longitude
    },
    geometryArea,
    geometry,
    graphic: processed
  };
}

/**
 * Throws when adding another shape would exceed the maximum feature count.
 */
export function assertDrawFeatureCountWithinLimit(
  existingFeatureCount: number,
  maxFeatureCount: number
): void {
  if (existingFeatureCount >= maxFeatureCount) {
    const err = new DrawingValidationError(
      `You can draw up to ${maxFeatureCount.toLocaleString('en-US')} features.`
    );
    err.name = 'Feature limit exceeded';
    throw err;
  }
}

/**
 * Throws when adding a new shape would push cumulative drawn area over the limit.
 * Uses buffered polygon area for polylines (call after validateAndProcessDrawing).
 */
export function assertCumulativeAreaWithinLimit(
  existingAreaKm2: number,
  newAreaKm2: number,
  maxCumulativeKm2: number
): void {
  const projectedTotal = existingAreaKm2 + newAreaKm2;
  if (projectedTotal > maxCumulativeKm2) {
    const err = new DrawingValidationError(
      `The area exceeds the maximum allowed size of ${maxCumulativeKm2} km². Please redraw a smaller area.`
    );
    err.name = 'Area limit exceeded';
    throw err;
  }
}

/**
 * Converts an ArcGIS polygon (Web Mercator) to WGS84 GeoJSON for Turf area.
 */
function arcGISPolygonToGeoJson(geometry: any, WebMercatorUtils: any): any {
  const geographicGeometry = WebMercatorUtils.webMercatorToGeographic(geometry);
  return Terraformer.parse(geographicGeometry);
}

let lastLiveAreaPerfLogAt = 0;

/**
 * When `window.__liveAreaPerfBenchmark = true`, logs ArcGIS vs Turf timings
 * (throttled) so you can compare live-drawing perf in the browser console.
 */
function maybeLogLiveAreaPerf(
  geometry: any,
  GeometryEngine: any,
  WebMercatorUtils: any
): void {
  if (!window.__liveAreaPerfBenchmark) return;

  const now = performance.now();
  if (now - lastLiveAreaPerfLogAt < 500) return;
  lastLiveAreaPerfLogAt = now;

  const arcgisStart = performance.now();
  const arcgisArea = GeometryEngine.geodesicArea(geometry, 'square-kilometers');
  const arcgisMs = performance.now() - arcgisStart;

  const turfStart = performance.now();
  const geoJson = arcGISPolygonToGeoJson(geometry, WebMercatorUtils);
  const turfArea =
    geoJson?.type === 'Polygon' ? computeGeoJsonAreaKm2(geoJson) : 0;
  const turfMs = performance.now() - turfStart;

  // eslint-disable-next-line no-console
  console.info('[live-area perf]', {
    arcgisMs: arcgisMs.toFixed(2),
    turfMs: turfMs.toFixed(2),
    deltaMs: (turfMs - arcgisMs).toFixed(2),
    arcgisAreaKm2: arcgisArea.toFixed(4),
    turfAreaKm2: turfArea.toFixed(4)
  });
}

/**
 * Computes area (km²) or length (km) for live-feedback while drawing.
 * Polygon area uses Turf on WGS84 GeoJSON (experimental — compare perf via
 * `window.__liveAreaPerfBenchmark = true` in the browser console).
 */
export function computeLiveArea(
  geometry: any,
  GeometryEngine: any,
  WebMercatorUtils: any
): number {
  if (geometry.type === 'polyline') {
    return GeometryEngine.geodesicLength(geometry, 'kilometers');
  }

  maybeLogLiveAreaPerf(geometry, GeometryEngine, WebMercatorUtils);

  const geoJson = arcGISPolygonToGeoJson(geometry, WebMercatorUtils);
  if (geoJson?.type === 'Polygon') {
    return computeGeoJsonAreaKm2(geoJson);
  }

  return GeometryEngine.geodesicArea(geometry, 'square-kilometers');
}
